import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsOverviewRoute} from './overview-route'
import {pullRequestsLayoutRoute} from './layout-route'
import type {LayoutRoutePayload, OverviewRoutePayload} from './route-payload-types'
import {SplitPageLayout} from '@primer/react'
import {Description} from '../components/overview/Description'
import {ChangeSummary} from '../components/overview/ChangeSummary'
import {Findings} from '../components/overview/Findings'
import {Activity} from '../components/overview/Activity'
import {OverviewPanel} from '../components/overview/OverviewPanel'
import {Reviewers} from '../components/overview/reviewers/Reviewers'
import {CollapsiblePanel} from '../components/overview/CollapsiblePanel'
import {
  DockedPanel,
  useDockedPanelActions,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../contexts/DockedPanelContext'
import {measureIsWideViewport} from '../helpers/measure-viewport'
import {useEffect} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ActionItemsSection} from '../components/overview/action-items/ActionItemsSection'
import {isPullRequestOpenState} from '../utils/pull-request-state'
import {CommentsSection} from '../components/overview/action-items/CommentsSection'

export function OverviewEntrypoint() {
  const {data: routeData} = useRouteQuery(pullRequestsOverviewRoute, 'mainQuery')
  const {data: layoutData} = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')

  return <OverviewComponent routeData={routeData} layoutData={layoutData} />
}

type OverviewComponentProps = {
  routeData: OverviewRoutePayload
  layoutData: LayoutRoutePayload
}

function OverviewComponent({routeData, layoutData}: OverviewComponentProps) {
  const dockedPanelActions = useDockedPanelActions()
  const isDockedPanelOpen = useIsDockedPanelOpen()
  const isCompact = useIsDockedPanelCompact()
  const isOverviewPanelEnabled = isFeatureEnabled('prs_overview_panel')
  const isActivityEnabled = isFeatureEnabled('prs_overview_catch_up')
  const isActionabilityFrameworkEnabled = isFeatureEnabled('prs_overview_actionability_framework')

  const {pullRequest} = layoutData
  const isPROpen = isPullRequestOpenState(pullRequest.state)

  useEffect(() => {
    if (isOverviewPanelEnabled && measureIsWideViewport()) {
      dockedPanelActions.openPanel(DockedPanel.Info)
    }
  }, [dockedPanelActions, isOverviewPanelEnabled])

  // Show sidebar when the flag is off, or on compact (mobile) viewports where we don't
  // auto-open the docked panel (and if manually opened it shows as a dialog overlay).
  const showMobileSidebar = !(isOverviewPanelEnabled && isDockedPanelOpen) || isCompact

  return (
    <>
      <SplitPageLayout.Content as="div" padding="condensed">
        <div className="d-flex gap-4 flex-column">
          <Description descriptionHtml={routeData.description} />
          {isPROpen && (
            <CollapsiblePanel heading="Action items">
              {isActionabilityFrameworkEnabled && <CommentsSection />}
              <ActionItemsSection />
            </CollapsiblePanel>
          )}
          {isActivityEnabled && <Activity />}
          <CollapsiblePanel heading="Reviews">
            <Reviewers />
          </CollapsiblePanel>
          <ChangeSummary changeHighlights={routeData.changeHighlights} />
          <Findings />
        </div>
      </SplitPageLayout.Content>
      {showMobileSidebar && (
        <SplitPageLayout.Pane position="end" sticky divider="none" padding="condensed" width="large">
          <OverviewPanel />
        </SplitPageLayout.Pane>
      )}
    </>
  )
}
